import { NavLink, Outlet, useNavigate } from 'react-router';
import { useMe, useSwitchRole, type Me, type Role } from '../lib/queries';
import { logout } from '../lib/auth';

/**
 * Which roles each destination is for. This mirrors the permission table in
 * the API (`services/authz.ts`) but does not replace it: the nav is a
 * convenience so leaders are not shown doors they cannot open, and every
 * endpoint enforces its own role regardless of what the client renders.
 */
const ALL: Role[] = ['owner', 'administrator', 'head_leader', 'networking_leader'];
const HQ: Role[] = ['owner', 'administrator'];
const LEADERSHIP: Role[] = ['owner', 'administrator', 'head_leader'];

const NAV: { to: string; label: string; end?: boolean; roles: Role[] }[] = [
  { to: '/', label: 'Dashboard', end: true, roles: ALL },
  { to: '/events', label: 'Events', roles: ALL },
  { to: '/orders', label: 'Orders', roles: ALL },
  { to: '/members', label: 'Members', roles: HQ },
  { to: '/businesses', label: 'Directory', roles: HQ },
  { to: '/business-categories', label: 'Categories', roles: HQ },
  { to: '/resources', label: 'Hub resources', roles: HQ },
  { to: '/venues', label: 'Venues', roles: LEADERSHIP },
  { to: '/regions', label: 'Regions', roles: HQ },
  { to: '/subscribers', label: 'Subscribers', roles: HQ },
  { to: '/newsletter', label: 'Newsletter', roles: HQ },
  { to: '/publish', label: 'Publish site', roles: HQ },
  { to: '/outbox', label: 'Email outbox', roles: HQ },
  { to: '/audit', label: 'Audit log', roles: HQ },
];

const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner',
  administrator: 'Administrator',
  head_leader: 'Head Leader',
  networking_leader: 'Networking Leader',
};

/** One entry per hat, so a Head Leader covering three regions gets three. */
function switchableHats(me: Me) {
  return me.assignments.map((a) => ({
    role: a.role,
    regionId: a.region_id,
    label: a.region_name ? `${ROLE_LABEL[a.role]} — ${a.region_name}` : ROLE_LABEL[a.role],
  }));
}

function ActingAs({ me }: { me: Me }) {
  const switchRole = useSwitchRole();
  const hats = switchableHats(me);

  if (!me.can_switch_role) {
    return <div className="acting-as-static">{ROLE_LABEL[me.acting_role]}</div>;
  }

  const current = hats.find((h) => h.role === me.acting_role);

  return (
    <label className="acting-as">
      <span className="acting-as-label">Acting as</span>
      <select
        className="acting-as-select"
        disabled={switchRole.isPending}
        value={`${current?.role ?? me.acting_role}:${current?.regionId ?? ''}`}
        onChange={(e) => {
          const [role, region] = e.target.value.split(':');
          switchRole.mutate({ role: role as Role, region_id: region ? Number(region) : null });
        }}
      >
        {hats.map((h) => (
          <option key={`${h.role}:${h.regionId ?? ''}`} value={`${h.role}:${h.regionId ?? ''}`}>
            {h.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AppShell() {
  const me = useMe();
  const navigate = useNavigate();

  // Until the role is known, render no nav rather than the full HQ tree — a
  // leader should never see an admin menu flash past on a slow connection.
  const role = me.data?.acting_role;
  const visible = role ? NAV.filter((n) => n.roles.includes(role)) : [];

  async function onSignOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="app">
      <aside className="sidebar">
        <div style={{ marginBottom: 24 }}>
          <h1 className="anton" style={{ fontSize: 32, color: 'var(--accent)' }}>MIB</h1>
          <div style={{ fontSize: 12, color: 'var(--text-on-dark-muted)', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>Admin</div>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {visible.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div style={{ marginTop: 'auto', paddingTop: 24 }}>
          {me.data && <ActingAs me={me.data} />}
          <div className="sidebar-identity">{me.data?.email ?? '…'}</div>
          <button type="button" className="sidebar-signout" onClick={onSignOut}>Sign out</button>
        </div>
      </aside>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
